import { useDataTableContext } from '../context';
import { DataTableColumnMeta } from '../types';
import { validateColumnFilterType } from '../utils';
import { CheckboxGroupFilter } from './checkbox-group-filter';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface ColumnCheckboxGroupFilterProps {
  columnId: string;
  /** Display label; suppressed when omitted (e.g. titled by parent). */
  label?: string;
  options: Array<{ value: string; label: string }>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column-bound checkbox-group filter that syncs with DataTable's filter state.
 * Automatically validates column type compatibility and manages filter value.
 * @param {ColumnCheckboxGroupFilterProps} props - Component props
 * @param {string} props.columnId - ID of the column to filter
 * @param {string} props.label - Display label for the filter group
 * @param {Array} props.options - Available options with value/label pairs
 * @throws {Error} If column type is not 'enum' or 'boolean'
 * @return {JSX.Element} The column checkbox group filter element
 */
export function ColumnCheckboxGroupFilter({
  columnId,
  label,
  options,
}: ColumnCheckboxGroupFilterProps) {
  const { table } = useDataTableContext();
  const column = table.getColumn(columnId);
  const meta = column?.columnDef.meta as DataTableColumnMeta | undefined;

  validateColumnFilterType('checkboxGroup', meta?.type, columnId);

  const value = (column?.getFilterValue() as string[]) ?? [];

  const handleChange = (newValue: string[]) => {
    column?.setFilterValue(newValue.length > 0 ? newValue : undefined);
  };

  return (
    <CheckboxGroupFilter
      id={`${columnId}-filter`}
      label={label}
      value={value}
      onChange={handleChange}
      options={options}
    />
  );
}
